import { EnrolmentAccessStatus } from "../types";

export const ACCESS_EXPIRED_ERROR = "Access has expired.";
export const TRIAL_RESTRICTED_CODE = "TRIAL_RESTRICTED";

let onAccessExpired: (() => void) | null = null;
let navigateToAccessExpired: (() => void) | null = null;

export function registerAccessExpiredHandler(handler: () => void) {
  onAccessExpired = handler;
}

export function registerAccessExpiredNavigator(navigate: () => void) {
  navigateToAccessExpired = navigate;
}

export function notifyAccessExpired() {
  onAccessExpired?.();
}

export function isAccessExpiredResponse(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return false;
  }

  const response = (error as { response?: { status?: number; data?: { error?: string } } }).response;
  return response?.status === 403 && response?.data?.error === ACCESS_EXPIRED_ERROR;
}

/** 403 for a feature that free-trial users cannot use (backend sends code TRIAL_RESTRICTED). */
export function isTrialRestrictedResponse(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return false;
  }

  const response = (error as { response?: { status?: number; data?: { code?: string } } }).response;
  return response?.status === 403 && response?.data?.code === TRIAL_RESTRICTED_CODE;
}

export function redirectToAccessExpiredPage() {
  if (window.location.pathname.endsWith("/access-expired")) {
    return;
  }

  if (navigateToAccessExpired) {
    navigateToAccessExpired();
    return;
  }

  const siteUrl = process.env.REACT_APP_SITE_URL?.replace(/\/$/, "") ?? "";
  window.location.href = `${siteUrl}/access-expired`;
}

export function normalizeAccessStatus(
  value: unknown,
): EnrolmentAccessStatus {
  if (value === "active" || value === "trial" || value === "expired" || value === "none") {
    return value;
  }

  return "none";
}

export function deriveAccessStatusFromEnrolments(
  enrolments: { isActive?: boolean; isTrial?: boolean }[],
): EnrolmentAccessStatus | null {
  if (enrolments.length === 0) {
    return null;
  }

  if (enrolments.every((enrolment) => enrolment.isActive === false)) {
    return "expired";
  }

  const usable = enrolments.filter((enrolment) => enrolment.isActive !== false);
  if (usable.some((enrolment) => !enrolment.isTrial)) {
    return "active";
  }

  if (usable.length > 0) {
    return "trial";
  }

  return null;
}

export function isContentAccessExpired(accessStatus: EnrolmentAccessStatus): boolean {
  return accessStatus === "expired";
}

/** Skip generic error toasts when the access-expired redirect or trial-upgrade toast will handle it. */
export function shouldShowErrorToast(error: unknown): boolean {
  return !isAccessExpiredResponse(error) && !isTrialRestrictedResponse(error);
}
